//
// Copyright 2024 DXOS.org
//

import { type EntityId } from '@dxos/keys';
import { isNonNullable } from '@dxos/util';

import { type AnyEntity } from '../common/types';
import { Ref } from './ref';

/**
 * Helper functions for working with arrays of refs.
 */
export const RefArray = Object.freeze({
  /**
   * @returns all resolved targets.
   */
  targets: <T extends AnyEntity>(refs: readonly Ref<T>[]): T[] => {
    return refs.map((ref) => ref.target).filter(isNonNullable);
  },

  /**
   * Load all referenced objects.
   */
  loadAll: <T extends AnyEntity>(refs: readonly Ref<T>[]): Promise<T[]> => {
    return Promise.all(refs.map((ref) => ref.load()));
  },

  /**
   * Removes the ref with the given id.
   */
  removeById: (refs: Ref<AnyEntity>[], id: EntityId) => {
    const index = refs.findIndex(Ref.hasEntityId(id));
    if (index >= 0) {
      refs.splice(index, 1);
    }
  },
});
